import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Trash, EnvelopeSimpleOpen, Phone, CurrencyDollar, PaperPlaneTilt, ChatCircle } from "@phosphor-icons/react";

const STATUSES = [
  { v: "new", l: "New" },
  { v: "in_talks", l: "In Talks" },
  { v: "won", l: "Won" },
  { v: "archived", l: "Archived" },
];

const statusStyle = (s) =>
  ({
    new: "bg-accent text-accent-foreground border-accent",
    in_talks: "bg-secondary text-foreground border-border",
    won: "bg-[hsl(140_25%_92%)] text-[hsl(140_35%_28%)] border-[hsl(140_25%_80%)]",
    archived: "bg-muted text-muted-foreground border-border",
  }[s] || "bg-secondary text-foreground border-border");

export default function EnquiriesPanel() {
  const [items, setItems] = useState([]);
  const [replyOpen, setReplyOpen] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);

  const load = () => api.get("/admin/contacts").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      const { data } = await api.patch(`/admin/contacts/${id}`, { status });
      setItems((prev) => prev.map((c) => (c.id === id ? data : c)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const sendReply = async (id) => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/admin/contacts/${id}/reply`, { message: replyText });
      setItems((prev) => prev.map((c) => (c.id === id ? data : c)));
      setReplyText("");
      setReplyOpen(null);
      toast.success("Reply sent to the enquirer");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    await api.delete(`/admin/contacts/${id}`);
    toast.success("Deleted");
    load();
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  const newCount = items.filter((i) => i.status === "new").length;

  return (
    <div className="max-w-4xl">
      <h2 className="font-serif text-3xl font-light">Enquiries</h2>
      <p className="text-sm text-muted-foreground mt-1">
        {items.length} total{newCount > 0 && <span className="text-accent"> · {newCount} new</span>} · emailed to you, with auto-reply to the enquirer
      </p>

      <div className="mt-8 space-y-4">
        {items.map((c) => (
          <div key={c.id} data-testid={`enquiry-${c.id}`} className={`border p-6 ${c.status === "new" ? "border-accent/50 bg-accent/5" : "border-border bg-card"}`}>
            <div className="flex items-start justify-between gap-4">
              <div>
                <h3 className="font-serif text-2xl">{c.name}</h3>
                <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-foreground/70">
                  <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 hover:text-foreground"><EnvelopeSimpleOpen size={15} /> {c.email}</a>
                  {c.phone && <span className="inline-flex items-center gap-1.5"><Phone size={15} /> {c.phone}</span>}
                  {c.budget && <span className="inline-flex items-center gap-1.5"><CurrencyDollar size={15} /> {c.budget}</span>}
                </div>
              </div>
              <div className="text-right shrink-0">
                {c.project_type && <span className="text-[10px] uppercase tracking-[0.2em] text-accent">{c.project_type}</span>}
                <p className="text-xs text-muted-foreground mt-1">{fmt(c.created_at)}</p>
              </div>
            </div>

            {c.message && <p className="mt-4 text-foreground/80 leading-relaxed whitespace-pre-line border-t border-border/60 pt-4">{c.message}</p>}

            {/* sent replies history */}
            {c.replies?.length > 0 && (
              <div className="mt-4 space-y-2">
                {c.replies.map((r, i) => (
                  <div key={i} className="bg-secondary/50 border border-border px-4 py-3 text-sm">
                    <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground mb-1">Replied by {r.by} · {fmt(r.at)}</div>
                    <div className="text-foreground/80 whitespace-pre-line">{r.message}</div>
                  </div>
                ))}
              </div>
            )}

            {/* controls */}
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-1.5" data-testid={`status-${c.id}`}>
                {STATUSES.map((s) => (
                  <button
                    key={s.v}
                    onClick={() => setStatus(c.id, s.v)}
                    className={`text-[11px] uppercase tracking-[0.1em] px-3 py-1.5 border rounded-full transition-colors ${
                      c.status === s.v ? statusStyle(s.v) : "border-border text-foreground/50 hover:text-foreground"
                    }`}
                  >
                    {s.l}
                  </button>
                ))}
              </div>
              <div className="ml-auto flex items-center gap-3">
                <button
                  onClick={() => { setReplyOpen(replyOpen === c.id ? null : c.id); setReplyText(""); }}
                  data-testid={`reply-toggle-${c.id}`}
                  className="inline-flex items-center gap-1.5 text-xs border border-border px-3 py-1.5 hover:bg-secondary transition-colors"
                >
                  <ChatCircle size={14} /> Reply
                </button>
                <button onClick={() => remove(c.id)} className="inline-flex items-center gap-1 text-xs text-destructive"><Trash size={14} /> Delete</button>
              </div>
            </div>

            {replyOpen === c.id && (
              <div className="mt-4 border-t border-border pt-4" data-testid={`reply-box-${c.id}`}>
                <textarea
                  data-testid={`reply-text-${c.id}`}
                  rows={4}
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  placeholder={`Write a reply to ${c.name}… (sent to ${c.email})`}
                  className="w-full bg-transparent border border-border px-3 py-2 text-sm focus:outline-none focus:border-foreground"
                />
                <div className="mt-3 flex justify-end">
                  <button
                    onClick={() => sendReply(c.id)}
                    disabled={sending}
                    data-testid={`reply-send-${c.id}`}
                    className="inline-flex items-center gap-2 bg-foreground text-background text-xs uppercase tracking-[0.15em] px-5 py-3 hover:bg-accent transition-colors disabled:opacity-50"
                  >
                    <PaperPlaneTilt size={15} /> {sending ? "Sending…" : "Send Reply"}
                  </button>
                </div>
              </div>
            )}
          </div>
        ))}
        {items.length === 0 && <p className="text-muted-foreground py-16 text-center">No enquiries yet.</p>}
      </div>
    </div>
  );
}
